import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  Activity, RefreshCw, Wrench, ShieldCheck, AlertTriangle,
  XCircle, Info, ChevronLeft,
} from 'lucide-react';

const SECTION_META = {
  reconciliation:    { title: 'مطابقة الدفاتر', desc: 'GL مقابل السجلات المساعدة (نقد، ذمم، ضريبة)' },
  vat_input_repair:  { title: 'اكتمال ضريبة المدخلات', desc: 'قيود VAT_INPUT مقابل المستندات + أرقام القيود المكررة' },
  integrity:         { title: 'سلامة الترحيل', desc: 'فواتير أو دفعات بلا قيود، وأسطر تكاليف يتيمة' },
  journal_links:     { title: 'ختم القيود التلقائي', desc: 'مستندات لها قيد مرحَّل غير مختوم عليها — تُختم تلقائياً عند كل فحص قبل أن تظهر كأخطاء' },
  recovery_readiness:{ title: 'جاهزية الاستعادة', desc: 'النسخ الاحتياطي واختبارات الاستعادة' },
  orphan_payments:   { title: 'دفعات بلا بنك', desc: 'دفعات معتمدة غير مربوطة بحساب بنكي' },
  reversals:         { title: 'القيود العكسية', desc: 'قيود الإلغاء الموثقة في سجل الإلغاءات' },
};

const STATUS_STYLE = {
  PASS:    { badge: 'bg-emerald-100 text-emerald-700', label: 'سليم',   Icon: ShieldCheck },
  WARNING: { badge: 'bg-amber-100 text-amber-700',     label: 'تحذير',  Icon: AlertTriangle },
  FAIL:    { badge: 'bg-red-100 text-red-700',         label: 'فشل',    Icon: XCircle },
  ERROR:   { badge: 'bg-red-100 text-red-700',         label: 'خطأ',    Icon: XCircle },
  INFO:    { badge: 'bg-slate-100 text-slate-600',     label: 'معلومة', Icon: Info },
  UNKNOWN: { badge: 'bg-slate-100 text-slate-600',     label: '—',      Icon: Info },
};

export default function DataHealthCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/data-health/overview');
      setData(res.data);
    } catch (e) {
      toast.error('فشل تحميل لوحة صحة البيانات');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const repairAll = async () => {
    if (!window.confirm('تشغيل كل محركات الإصلاح الذاتي الآن؟')) return;
    setRepairing(true);
    try {
      const res = await axiosInstance.post('/data-health/repair-all');
      const v = res.data?.results?.vat_input_repair || {};
      toast.success(
        `اكتمل الإصلاح — قيود ضريبة: ${v.repaired ?? 0}، ` +
        `أرقام مكررة: ${v.renumbered ?? 0}، قيود مفقودة: ${v.missing_journals ?? 0}`);
      fetchData();
    } catch (e) {
      toast.error('فشل تشغيل الإصلاح الشامل');
    } finally {
      setRepairing(false);
    }
  };

  const overall = data?.overall || 'UNKNOWN';
  const OS = STATUS_STYLE[overall] || STATUS_STYLE.UNKNOWN;

  return (
    <div dir="rtl" className="space-y-6" data-testid="data-health-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-indigo-600" />
            لوحة صحة البيانات الموحدة
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            كل أدوات الفحص والإصلاح في شاشة واحدة — حالة النظام بنظرة واحدة.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} data-testid="health-refresh-btn"
            className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            تحديث
          </button>
          <button onClick={repairAll} disabled={repairing}
            data-testid="health-repair-all-btn"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition-colors disabled:opacity-50">
            <Wrench className={`w-4 h-4 ${repairing ? 'animate-spin' : ''}`} />
            إصلاح شامل
          </button>
        </div>
      </div>

      <div className={`rounded-xl border p-4 flex items-center gap-3 ${
        overall === 'PASS' ? 'bg-emerald-50 border-emerald-200'
        : overall === 'WARNING' ? 'bg-amber-50 border-amber-200'
        : 'bg-red-50 border-red-200'}`} data-testid="health-overall-banner">
        <OS.Icon className="w-8 h-8" />
        <div>
          <p className="font-bold text-gray-900">
            الحالة العامة: {OS.label}
          </p>
          <p className="text-xs text-gray-600">
            {data ? `سليم ${data.counts?.PASS ?? 0} · تحذير ${data.counts?.WARNING ?? 0} · فشل ${(data.counts?.FAIL ?? 0) + (data.counts?.ERROR ?? 0)}` : '…'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {loading && !data ? (
          <p className="text-gray-400 py-8">جارٍ الفحص…</p>
        ) : (data?.sections || []).map((s) => {
          const meta = SECTION_META[s.name] || { title: s.name, desc: '' };
          const st = STATUS_STYLE[s.status] || STATUS_STYLE.UNKNOWN;
          return (
            <div key={s.name}
              className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col gap-2 hover:shadow-md transition-shadow"
              data-testid={`health-card-${s.name}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{meta.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${st.badge}`}>
                  {st.label}
                </span>
              </div>
              <p className="text-xs text-gray-500">{meta.desc}</p>
              <p className="text-xl font-bold text-gray-800 tabular-nums" data-testid={`health-metric-${s.name}`}>
                {s.metric}
              </p>
              {s.link && (
                <button onClick={() => navigate(s.link)}
                  className="mt-auto self-start text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1">
                  فتح الأداة التفصيلية
                  <ChevronLeft className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
